// Radius mode
// Source:
// https://gist.github.com/chriswhong/694779bc1f1e5d926e47bab7205fa559
// custom mapbopx-gl-draw mode that modifies draw_line_string
// shows a center point, radius line, and circle polygon while drawing
// forces draw.create on creation of second vertex
/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-this-in-sfc */
/* eslint-disable func-names */
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import _ from 'lodash';
import lineDistance from '@turf/length';

const DrawCircle = _.extend({}, MapboxDraw.modes.draw_line_string);

// create a circle-like polygon given a center point and radius
// https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js/39006388#39006388
function createGeoJSONCircle(line, points = 64) {
    const geojson = line.toGeoJSON();
    const centre = geojson.geometry.coordinates[0];
    const radiusInKm = lineDistance(geojson, { units: 'kilometers' });

    const coords = {
        latitude: centre[1],
        longitude: centre[0]
    };

    const km = radiusInKm;

    const ret = [];
    const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    const distanceY = km / 110.574;

    let theta;
    let x;
    let y;
    for (let i = 0; i < points; i += 1) {
        theta = (i / points) * (2 * Math.PI);
        x = distanceX * Math.cos(theta);
        y = distanceY * Math.sin(theta);

        ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    return {
        type: 'Feature',
        geometry: {
            type: 'Polygon',
            coordinates: [ret]
        },
        properties: {
            parent: line.id,
            active: 'true',
            radius: radiusInKm * 1000,
            isCircle: true,
            center: centre
        }
    };
}

const doubleClickZoom = {
    enable: ctx => {
        setTimeout(() => {
            // First check we've got a map and some context.
            if (
                !ctx.map ||
                !ctx.map.doubleClickZoom ||
                !ctx._ctx ||
                !ctx._ctx.store ||
                !ctx._ctx.store.getInitialConfigValue
            ) {
                return;
            }
            // Now check initial state wasn't false (we leave it disabled if so)
            if (!ctx._ctx.store.getInitialConfigValue('doubleClickZoom')) return;
            ctx.map.doubleClickZoom.enable();
        }, 0);
    }
};

DrawCircle.onSetup = function (opts) {
    let result = MapboxDraw.modes.draw_line_string.onSetup.call(this, null);

    return result;
};

DrawCircle.clickAnywhere = function (state, e) {
    // this ends the drawing after the user creates a second point, triggering this.onStop

    if (state.currentVertexPosition === 1) {
        state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
        return this.changeMode('simple_select', { featureIds: [state.line.id] });
    }
    return MapboxDraw.modes.draw_line_string.clickAnywhere.call(this, state, e);
};

// creates the final geojson point feature with a radius property
// triggers draw.create
DrawCircle.onStop = function (state) {
    doubleClickZoom.enable(this);

    this.activateUIButton();

    // check to see if we've deleted this feature
    if (this.getFeature(state.line.id) === undefined) {
        this.map.fire('draw.cancelled');
        return;
    }

    // remove last added coordinate
    state.line.removeCoordinate('0');
    if (state.line.isValid()) {
        const circleGeoJSON = createGeoJSONCircle(state.line);
        if (state.circleHeight) {
            circleGeoJSON.properties.height = state.circleHeight;
        }

        const id = state.line.id;
        this.deleteFeature(id, { silent: true });
        delete circleGeoJSON.properties.parent;
        const circleFeature = this.newFeature(circleGeoJSON);
        circleFeature.id = id;
        this.addFeature(circleFeature);

        this.map.fire('draw.create', {
            features: [circleGeoJSON]
        });
    } else {
        this.deleteFeature(state.line.id, { silent: true });
        this.changeMode('simple_select', {}, { silent: true });
    }
};

DrawCircle.toDisplayFeatures = function (state, geojson, display) {
    if (geojson.properties.id === state.line.id) {
        geojson.properties.active = 'true';

        // Only render the line if it has at least one real coordinate
        if (geojson.geometry.coordinates.length < 2) return;
        geojson.properties.meta = 'feature';

        // displays center vertex as a point feature
        display(
            MapboxDraw.lib.createVertex(
                state.line.id,
                geojson.geometry.coordinates[
                    state.direction === 'forward' ? geojson.geometry.coordinates.length - 2 : 1
                ],
                `${state.direction === 'forward' ? geojson.geometry.coordinates.length - 2 : 1}`,
                false
            )
        );

        // displays the radius line and circle as they are drawn
        display(geojson);
        const circleGeoJSON = createGeoJSONCircle(state.line);
        // if (state.circleHeight) {
        //     // we need the 'user_' prefix for display features
        //     circleGeoJSON.properties.user_height = state.circleHeight;
        // }
        display(circleGeoJSON);

        this.map.fire('draw.editing', {
            features: [circleGeoJSON]
        });
    } else {
        geojson.properties.active = 'false';
        display(geojson);
    }
};

export default DrawCircle;
